import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  Users, Plus, Edit3, Trash2, Lock, Save, Loader2, X, UserPlus, Shield, Eye, EyeOff
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminUsersView = ({ auth }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [createForm, setCreateForm] = useState({ username: "", password: "" });
  const [editForm, setEditForm] = useState({ username: "", password: "" });
  const [pwForm, setPwForm] = useState({ current: "", newPw: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [showPw, setShowPw] = useState({});

  const authConfig = { auth: { username: auth.username, password: auth.password } };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, authConfig);
      setUsers(res.data.users || []);
    } catch (e) {
      toast.error("Error cargando usuarios");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/admin/users`, createForm, authConfig);
      toast.success(`Admin '${createForm.username}' creado`);
      setCreateForm({ username: "", password: "" });
      setShowCreate(false);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error creando usuario");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {};
    if (editForm.username && editForm.username !== editingUser) payload.username = editForm.username;
    if (editForm.password) payload.password = editForm.password;
    try {
      await axios.put(`${API}/admin/users/${editingUser}`, payload, authConfig);
      toast.success("Usuario actualizado");
      setEditingUser(null);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error actualizando");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (username) => {
    if (!window.confirm(`Eliminar admin '${username}'?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${username}`, authConfig);
      toast.success(`Admin '${username}' eliminado`);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error eliminando");
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.newPw !== pwForm.confirm) {
      toast.error("Las contrasenas no coinciden");
      return;
    }
    setPwSaving(true);
    try {
      await axios.post(`${API}/admin/users/change-password`, {
        current_password: pwForm.current,
        new_password: pwForm.newPw,
      }, authConfig);
      toast.success("Contrasena actualizada. Cierra sesion y vuelve a entrar.");
      setPwForm({ current: "", newPw: "", confirm: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error cambiando contrasena");
    } finally {
      setPwSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-[#3b82f6] animate-spin" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-white mb-1">Gestion de Administradores</h1>
        <p className="text-[#94a3b8] text-sm">Administra los usuarios que tienen acceso al panel</p>
      </div>

      {/* Users List */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-white font-semibold flex items-center gap-2">
            <Users className="w-5 h-5 text-[#3b82f6]" /> Administradores ({users.length})
          </h2>
          <motion.button
            onClick={() => setShowCreate(true)}
            className="bg-[#3b82f6] text-white text-sm font-medium px-4 py-2 flex items-center gap-2"
            whileHover={{ boxShadow: "0 0 20px rgba(59,130,246,0.4)" }}
            data-testid="add-admin-btn"
          >
            <UserPlus className="w-4 h-4" /> Agregar Admin
          </motion.button>
        </div>

        <div className="space-y-3">
          {users.map((user) => (
            <div
              key={user.username}
              className="flex items-center justify-between p-4 bg-white/5 border border-white/5 rounded"
              data-testid={`admin-user-${user.username}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#3b82f6]/10 flex items-center justify-center text-[#3b82f6] font-bold text-sm">
                  {user.username.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-white font-medium">{user.username}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-[#06b6d4] bg-[#06b6d4]/10 px-1.5 py-0.5 flex items-center gap-1">
                      <Shield className="w-2.5 h-2.5" /> Admin
                    </span>
                    {user.created_at && (
                      <span className="text-[#94a3b8] text-[10px]">
                        Creado: {new Date(user.created_at).toLocaleDateString("es")}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setEditingUser(user.username); setEditForm({ username: user.username, password: "" }); }}
                  className="text-[#94a3b8] hover:text-[#3b82f6] transition-colors p-2"
                  data-testid={`edit-admin-${user.username}`}
                  title="Editar"
                >
                  <Edit3 className="w-4 h-4" />
                </button>
                {user.username !== auth.username && (
                  <button
                    onClick={() => handleDelete(user.username)}
                    className="text-[#94a3b8] hover:text-red-400 transition-colors p-2"
                    data-testid={`delete-admin-${user.username}`}
                    title="Eliminar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Create Admin Modal */}
      <AnimatePresence>
        {showCreate && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
              onClick={() => setShowCreate(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="bg-[#0b1221] border border-white/10 p-6 w-full max-w-md rounded" data-testid="create-admin-modal">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-white font-semibold">Nuevo Administrador</h3>
                  <button onClick={() => setShowCreate(false)} className="text-[#94a3b8] hover:text-white">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <form onSubmit={handleCreate} className="space-y-4">
                  <div>
                    <label className="block text-[#94a3b8] text-xs mb-1.5">Nombre de usuario *</label>
                    <input
                      value={createForm.username}
                      onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
                      required
                      minLength={3}
                      placeholder="usuario_admin"
                      className="w-full bg-[#02040a] border border-white/10 px-4 py-2.5 text-white text-sm focus:border-[#3b82f6] focus:outline-none"
                      data-testid="create-admin-username"
                    />
                  </div>
                  <div>
                    <label className="block text-[#94a3b8] text-xs mb-1.5">Contrasena *</label>
                    <div className="relative">
                      <input
                        type={showPw.create ? "text" : "password"}
                        value={createForm.password}
                        onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                        required
                        minLength={6}
                        placeholder="Min. 6 caracteres"
                        className="w-full bg-[#02040a] border border-white/10 px-4 pr-10 py-2.5 text-white text-sm focus:border-[#3b82f6] focus:outline-none"
                        data-testid="create-admin-password"
                      />
                      <button type="button" onClick={() => setShowPw({ ...showPw, create: !showPw.create })} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8]">
                        {showPw.create ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button type="button" onClick={() => setShowCreate(false)} className="flex-1 border border-white/10 text-[#94a3b8] py-2.5 text-sm hover:text-white transition-colors">
                      Cancelar
                    </button>
                    <motion.button
                      type="submit"
                      disabled={saving}
                      className="flex-1 bg-[#3b82f6] text-white py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                      whileHover={{ boxShadow: "0 0 20px rgba(59,130,246,0.4)" }}
                      data-testid="create-admin-submit"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                      Crear Admin
                    </motion.button>
                  </div>
                </form>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Edit Admin Modal */}
      <AnimatePresence>
        {editingUser && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
              onClick={() => setEditingUser(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="bg-[#0b1221] border border-white/10 p-6 w-full max-w-md rounded" data-testid="edit-admin-modal">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-white font-semibold">Editar: {editingUser}</h3>
                  <button onClick={() => setEditingUser(null)} className="text-[#94a3b8] hover:text-white">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <form onSubmit={handleEdit} className="space-y-4">
                  <div>
                    <label className="block text-[#94a3b8] text-xs mb-1.5">Nombre de usuario</label>
                    <input
                      value={editForm.username}
                      onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                      minLength={3}
                      className="w-full bg-[#02040a] border border-white/10 px-4 py-2.5 text-white text-sm focus:border-[#3b82f6] focus:outline-none"
                      data-testid="edit-admin-username"
                    />
                  </div>
                  <div>
                    <label className="block text-[#94a3b8] text-xs mb-1.5">Nueva contrasena (dejar vacio para no cambiar)</label>
                    <div className="relative">
                      <input
                        type={showPw.edit ? "text" : "password"}
                        value={editForm.password}
                        onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                        placeholder="Dejar vacio para no cambiar"
                        className="w-full bg-[#02040a] border border-white/10 px-4 pr-10 py-2.5 text-white text-sm focus:border-[#3b82f6] focus:outline-none"
                        data-testid="edit-admin-password"
                      />
                      <button type="button" onClick={() => setShowPw({ ...showPw, edit: !showPw.edit })} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8]">
                        {showPw.edit ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button type="button" onClick={() => setEditingUser(null)} className="flex-1 border border-white/10 text-[#94a3b8] py-2.5 text-sm hover:text-white transition-colors">
                      Cancelar
                    </button>
                    <motion.button
                      type="submit"
                      disabled={saving}
                      className="flex-1 bg-[#3b82f6] text-white py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                      whileHover={{ boxShadow: "0 0 20px rgba(59,130,246,0.4)" }}
                      data-testid="edit-admin-submit"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Guardar
                    </motion.button>
                  </div>
                </form>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Change Own Password */}
      <div className="glass-card p-6">
        <h2 className="text-white font-semibold flex items-center gap-2 mb-5">
          <Lock className="w-5 h-5 text-[#3b82f6]" /> Cambiar Mi Contrasena
        </h2>
        <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
          <div>
            <label className="block text-[#94a3b8] text-xs mb-1.5">Contrasena actual *</label>
            <input
              type="password"
              value={pwForm.current}
              onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
              required
              className="w-full bg-[#02040a] border border-white/10 px-4 py-2.5 text-white text-sm focus:border-[#3b82f6] focus:outline-none"
              data-testid="pw-current"
            />
          </div>
          <div>
            <label className="block text-[#94a3b8] text-xs mb-1.5">Nueva contrasena *</label>
            <input
              type="password"
              value={pwForm.newPw}
              onChange={(e) => setPwForm({ ...pwForm, newPw: e.target.value })}
              required
              minLength={6}
              className="w-full bg-[#02040a] border border-white/10 px-4 py-2.5 text-white text-sm focus:border-[#3b82f6] focus:outline-none"
              data-testid="pw-new"
            />
          </div>
          <div>
            <label className="block text-[#94a3b8] text-xs mb-1.5">Confirmar nueva contrasena *</label>
            <input
              type="password"
              value={pwForm.confirm}
              onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
              required
              minLength={6}
              className="w-full bg-[#02040a] border border-white/10 px-4 py-2.5 text-white text-sm focus:border-[#3b82f6] focus:outline-none"
              data-testid="pw-confirm"
            />
          </div>
          <motion.button
            type="submit"
            disabled={pwSaving}
            className="bg-[#3b82f6] text-white text-sm font-semibold px-6 py-2.5 flex items-center gap-2 disabled:opacity-50"
            whileHover={{ boxShadow: "0 0 20px rgba(59,130,246,0.4)" }}
            data-testid="pw-save-btn"
          >
            {pwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            Cambiar Contrasena
          </motion.button>
        </form>
      </div>
    </motion.div>
  );
};

export default AdminUsersView;
